import React, { FC, useEffect, useState } from "react"
import "../styles/smartlink.css"
import { Release, SmartLink, SmartLinkAnalytics, SmartLinkPreSave, SmartLinkReleaseAnalytics } from "../../../../types"
import { KSelectRange, KSelectRangeDate, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { mapToCamelCase, toDashedYYYYMMDD } from "../../../../utility"
import { releases } from "../../../../apis"
import { getLanguageJson, getSelectedLanguage, lang } from "../../../../constants/languages"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../../constants"
import Loading from "../../../ui/Loading"
import { Doughnut, Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js"

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler)

// --- Extra types coming back from the analytics endpoint ---
// TODO: move these into ../../../../types alongside SmartLinkReleaseAnalytics
type TimelinePoint = { date: string; clicks: number; songPreviews: number; clicksToService: number }
type StreamEntry = { name: string; clicks: number; songPreviews: number; clicksToService: number }
type LocationEntry = { code: string; clicks: number; songPreviews: number; clicksToService: number }

type ExtendedAnalytics = SmartLinkReleaseAnalytics & {
  timeline?: TimelinePoint[]
  streams?: StreamEntry[]
  locations?: LocationEntry[]
}

type AnalyticPageProps = {
  release: Release
}

const toLocaleDate = (date: Date) => {
  return date.toLocaleDateString(getSelectedLanguage(), { day: "2-digit", month: "short", year: "numeric" })
}

const PIE_COLORS = ["#4CAF50", "#FF9800", "#E91E8C", "#3F51B5", "#00BCD4", "#9C27B0", "#ff0000", "#c3e848"]

const SERVICE_ICONS: Record<string, string> = {
  spotify: "/account_icons/social_media_icons/spotify.svg",
  soundcloud: "/account_icons/social_media_icons/soundcloud.svg",
  apple: "/account_icons/social_media_icons/apple-music.svg",
  tidal: "/account_icons/social_media_icons/tidal.png",
  deezer: "/account_icons/social_media_icons/deezer.png",
  amazon: "/account_icons/social_media_icons/amazon.svg",
  youtube: "/account_icons/social_media_icons/youtube.svg",
  itunes: "/account_icons/social_media_icons/iTunes.png"
}

const SERVICE_COLORS: Record<string, string> = {
  spotify: "#4CAF50",
  soundcloud: "#FF9800",
  apple: "#E91E8C",
  tidal: "#3F51B5",
  deezer: "#9C27B0",
  amazon: "#00BCD4",
  youtube: "#ff0000",
  itunes: "#8900ac"
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const codeToFlagEmoji = (code: string) =>
  code.toUpperCase().replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))

type BreakdownItem = {
  label: string
  value: number
  icon?: string
  emoji?: string
  color?: string
  secondaryValue?: number
}

const DonutBreakdown: FC<{
  title: string
  items: BreakdownItem[]
  valueLabel?: string
  secondaryValueLabel?: string
}> = ({ title, items, valueLabel, secondaryValueLabel }) => {
  const sorted = [...items].sort((a, b) => b.value - a.value)
  const total = sorted.reduce((sum, i) => sum + i.value, 0)
  const centerIcon = sorted[0]?.icon
  const centerEmoji = sorted[0]?.emoji
  const hasSecondary = sorted.some((i) => i.secondaryValue !== undefined)

  const colorFor = (item: BreakdownItem, idx: number) => item.color || PIE_COLORS[idx % PIE_COLORS.length]

  const data = {
    labels: sorted.map((i) => i.label),
    datasets: [
      {
        data: sorted.map((i) => i.value),
        backgroundColor: sorted.map((item, idx) => colorFor(item, idx)),
        borderWidth: 0
      }
    ]
  }

  const options = {
    cutout: "68%",
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#fff"
      }
    }
  }

  return (
    <div className="analytic-div" style={{ width: "100%" }}>
      <KTitleSpan text={title} fontSize={20} />
      <div className="mt-4 flex items-center gap-8">
        <div style={{ flex: 1 }}>
          {hasSecondary && (
            <div className="flex justify-between items-center" style={{ padding: "0 0 8px 0" }}>
              <div style={{ marginLeft: 330 }}>
                <KSpan text={secondaryValueLabel || "Secondary"} fontSize={12} color="#888" />
              </div>
              <div className="mr-2">
                <KSpan text={valueLabel || "Value"} fontSize={12} color="#888" />
              </div>
            </div>
          )}
          {sorted.map((item, idx) => (
            <div
              key={item.label}
              className="flex justify-between items-center"
              style={{
                padding: "12px 0",
                borderBottom: idx !== sorted.length - 1 ? "1px solid #eee" : "none"
              }}
            >
              <div className="flex items-center gap-2">
                {item.icon && <img src={item.icon} width={20} />}
                {item.emoji && <span style={{ fontSize: 18 }}>{item.emoji}</span>}
                <KSpan color="#000" fontWeight={500} text={item.label} />
              </div>
              <div className="flex items-center gap-2">
                {item.secondaryValue !== undefined && (
                  <div style={{ width: 90 }}>
                    <KSpan text={item.secondaryValue.toString()} />
                  </div>
                )}
                <KSpan text={item.value.toString()} />
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    backgroundColor: colorFor(item, idx)
                  }}
                />
                <KSpan text={total ? `${((item.value / total) * 100).toFixed(1)}%` : "0%"} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ width: 220, height: 220, position: "relative", flexShrink: 0 }}>
          <Doughnut data={data} options={options} />
          {(centerIcon || centerEmoji) && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {centerIcon ? <img src={centerIcon} width={40} /> : <span style={{ fontSize: 32 }}>{centerEmoji}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const TimelineChart: FC<{ data: TimelinePoint[] }> = ({ data }) => {
  const chartData = {
    labels: data.map((d) => d.date),
    datasets: [
      {
        label: lang.catalog.smart_link.analytics_page.visits,
        data: data.map((d) => d.clicks),
        borderColor: "#4CAF50",
        backgroundColor: "rgba(76,175,80,0.1)",
        tension: 0.3,
        fill: true
      },
      {
        label: lang.catalog.smart_link.analytics_page.song_previews,
        data: data.map((d) => d.songPreviews),
        borderColor: "#FF9800",
        backgroundColor: "rgba(255,152,0,0.1)",
        tension: 0.3,
        fill: true
      },
      {
        label: lang.catalog.smart_link.analytics_page.clicks_to_service,
        data: data.map((d) => d.clicksToService),
        borderColor: "#E91E8C",
        backgroundColor: "rgba(233,30,140,0.1)",
        tension: 0.3,
        fill: true
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: { legend: { position: "bottom" as const } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
  }

  return (
    <div style={{ height: 300, width: "100%" }}>
      <Line data={chartData} options={options} />
    </div>
  )
}

const AnalyticPage: FC<AnalyticPageProps> = (props) => {
  const { release } = props

  const today = new Date()
  const oneMonthBefore = new Date(today)
  oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1)

  const [smartLinkAnalytic, setSmartLinkAnalytic] = useState<ExtendedAnalytics>()
  const [dateValues, setDateValues] = useState<DateRangeType>([oneMonthBefore, today])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    //@ts-ignore
    const start = toDashedYYYYMMDD(dateValues[0])
    //@ts-ignore
    const end = toDashedYYYYMMDD(dateValues[1])

    setLoading(true)
    releases(`/release/fm_smart_link_analytics/?release_id=${release.id}&start_date=${start}&end_date=${end}`, "get")
      .then((resp) => {
        setLoading(false)
        if (resp && resp.data) {
          setSmartLinkAnalytic(mapToCamelCase(resp.data))
        } else {
          toast.error("Error fetching smart link analytics. Please contact support.", errorFilledToastOptions)
        }
      })
      .catch(() => {
        toast.error("Error fetching smart link analytics. Please contact support.", errorFilledToastOptions)
        setLoading(false)
      })
  }, [dateValues])

  const streamItems: BreakdownItem[] =
    smartLinkAnalytic?.streams?.map((s) => ({
      label: capitalize(s.name),
      value: s.clicksToService,
      icon: SERVICE_ICONS[s.name],
      color: SERVICE_COLORS[s.name]
    })) || []

  const locationItems: BreakdownItem[] =
    smartLinkAnalytic?.locations?.map((l) => ({
      label: l.code,
      value: l.clicksToService,
      secondaryValue: l.clicks, // "Visits"
      emoji: codeToFlagEmoji(l.code)
    })) || []

  return (
    <div style={{ minWidth: 800, height: "80vh", overflowY: "auto" }}>
      {loading && <Loading />}
      <div className="flex justify-between ">
        <KTitleSpan text={lang.catalog.smart_link.analytics_title} fontSize={32} />
        <div className="flex gap-4">
          <div className="flex items-center">
            {
              //@ts-ignore
              dateValues?.[0] && <KSpan text={`${toLocaleDate(dateValues[0])} - ${toLocaleDate(dateValues[1])}`} />
            }
          </div>
          <KSelectRangeDate
            key={`date_filter`}
            value={dateValues}
            onChange={(event) => setDateValues(event)}
            height="36px"
            width="auto"
            backgroundColor="#F7F7F7"
            hoverBackgroundColor="#F3F3F3"
            boxShadow="none"
            align={"center"}
          />
        </div>
      </div>

      <div className="mt-8">
        <KTitleSpan text={lang.catalog.smart_link.analytics_page.totals} fontSize={20} />
      </div>
      <div className="mt-4 flex gap-2" style={{ width: "100%" }}>
        <div className="analytic-count" style={{ width: "25%" }}>
          <div className="flex gap-1">
            <img src="/account_icons/eye.svg" width={20} />

            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.visits} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.totals.clicks?.toString() || " - "} fontSize={32} />
          </div>
        </div>
        <div className="analytic-count" style={{ width: "25%" }}>
          <div className="flex gap-1">
            <img src="/account_icons/user-circle.svg" width={20} />
            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.unique_users} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.totals.uniqueUsers?.toString() || " - "} fontSize={32} />
          </div>
        </div>
        <div className="analytic-count" style={{ width: "25%" }}>
          <div className="flex gap-1">
            <img src="/analytics_icons/headphones.svg" width={20} />
            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.song_previews} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.totals?.songPreviews?.toString() || " - "} fontSize={32} />
          </div>
        </div>
        <div className="analytic-count" style={{ width: "25%" }}>
          <div className="flex gap-1">
            <img src="/analytics_icons/link.svg" width={20} />
            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.clicks_to_service} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.totals.clicksToService?.toString() || " - "} fontSize={32} />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <KTitleSpan text={"Pre-Save"} fontSize={20} />
      </div>
      <div className="mt-4 flex gap-2" style={{ width: "100%" }}>
        <div className="analytic-count" style={{ width: "33.3%" }}>
          <div className="flex gap-1">
            <img src="/account_icons/presentation-chart.svg" width={20} />
            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.total} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.preSave.total?.toString() || " - "} fontSize={32} />
          </div>
        </div>
        <div className="analytic-count" style={{ width: "33.3%" }}>
          <div className="flex gap-1">
            <img src="/account_icons/sparkle-grey.svg" width={20} />
            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.one_time} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.preSave.oneTime?.toString() || " - "} fontSize={32} />
          </div>
        </div>
        <div className="analytic-count" style={{ width: "33.3%" }}>
          <div className="flex gap-1">
            <img src="/account_icons/play.svg" width={20} />
            <KSpan color="#000" fontWeight={500} text={lang.catalog.smart_link.analytics_page.forever} />
          </div>
          <div className="mt-8">
            <KTitleSpan text={smartLinkAnalytic?.preSave.forever?.toString() || " - "} fontSize={32} />
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-8">
        <KTitleSpan text={lang.catalog.smart_link.analytics_page.timeline} fontSize={20} />
      </div>
      <div className="mt-4 analytic-div" style={{ width: "100%" }}>
        {smartLinkAnalytic?.timeline && smartLinkAnalytic.timeline.length > 0 ? (
          <TimelineChart data={smartLinkAnalytic.timeline} />
        ) : (
          <KSpan text={lang.catalog.smart_link.analytics_page.no_timeline_data} />
        )}
      </div>

      {/* Streams + Location */}
      <div className="mt-8 flex gap-4" style={{ width: "100%" }}>
        {streamItems.length > 0 ? (
          <DonutBreakdown title={lang.catalog.smart_link.analytics_page.platforms_clicks} items={streamItems} />
        ) : (
          <div className="mt-4 analytic-div" style={{ width: "100%" }}>
            <KSpan text={lang.catalog.smart_link.analytics_page.no_stream_data} />
          </div>
        )}
      </div>

      <div className="mt-8 flex gap-4" style={{ width: "100%" }}>
        {locationItems.length > 0 ? (
          <DonutBreakdown
            title={lang.catalog.smart_link.analytics_page.location}
            items={locationItems}
            valueLabel={lang.catalog.smart_link.analytics_page.clicks}
            secondaryValueLabel={lang.catalog.smart_link.analytics_page.visits}
          />
        ) : (
          <div className="mt-4 analytic-div" style={{ width: "100%" }}>
            <KSpan text={lang.catalog.smart_link.analytics_page.no_location_data} />
          </div>
        )}
      </div>
    </div>
  )
}

export default AnalyticPage
